import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { APPLICATION_KEY } from "../../../Configurations/Urls";
import {
  assignApplicationToOrganization,
  deactivateOrganizationApplication,
  fetchClientsForOrgAppAccess,
  fetchOrgAppAccessContext,
  reactivateOrganizationApplication,
} from "../../../Apis/OrganizationApplicationAccess";

function buildRows(context) {
  const rows = [];
  for (const org of context?.organizations || []) {
    for (const app of org.linked_applications || []) {
      rows.push({
        organization_id: Number(org.organization_id),
        organization_name: org.organization_name,
        organization_short_name: org.organization_short_name,
        application_id: Number(app.application_id),
        application_name: app.application_name,
        is_active: !!app.is_active,
        link_exists: true,
      });
    }
  }
  return rows.sort((a, b) => {
    const orgCmp = a.organization_name.localeCompare(b.organization_name);
    if (orgCmp !== 0) return orgCmp;
    return a.application_name.localeCompare(b.application_name);
  });
}

export default function OrganizationApplicationAccess() {
  const { userData, logout } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const navigate = useNavigate();
  const canView = Array.isArray(Permission) && Permission.includes("VIEW_ORGANIZATION_APPLICATION");
  const canAdd = Array.isArray(Permission) && Permission.includes("ADD_ORGANIZATION_APPLICATION");
  const canChangeStatus = Array.isArray(Permission) && Permission.includes("CHANGE_STATUS");
  const loggedInClientId = useMemo(() => {
    const id =
      userData?.client_id ??
      userData?.client?.client_id ??
      userData?.Client?.client_id ??
      null;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [userData]);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState(null);
  const [updatingKey, setUpdatingKey] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!canView) {
      setClients([]);
      setSelectedClientId("");
      setContext(null);
      setClientsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setClientsLoading(true);
      try {
        const list = await fetchClientsForOrgAppAccess();
        if (!cancelled) {
          const safe = list || [];
          if (loggedInClientId) {
            const mine = safe.filter((c) => Number(c.client_id) === loggedInClientId);
            setClients(mine);
            setSelectedClientId(mine.length ? String(mine[0].client_id) : "");
          } else {
            setClients(safe);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.error || err.message || "Failed to load clients.");
        }
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedInClientId, canView]);

  const loadContext = useCallback(async (clientId) => {
    if (!clientId) {
      setContext(null);
      return;
    }
    setContextLoading(true);
    setContextError(null);
    try {
      setContext(await fetchOrgAppAccessContext(clientId));
    } catch (err) {
      setContext(null);
      setContextError(
        err.response?.data?.error || err.message || "Failed to load organization application access."
      );
    } finally {
      setContextLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) {
      setContext(null);
      return;
    }
    if (!selectedClientId) {
      setContext(null);
      return;
    }
    loadContext(Number(selectedClientId));
  }, [selectedClientId, loadContext, canView]);

  const rows = useMemo(() => buildRows(context), [context]);
  const clientOwnedApps = useMemo(
    () => (context?.assigned_applications || []).filter((a) => a.is_active),
    [context]
  );
  const linkedRowSet = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => {
      if (r.link_exists) s.add(`${r.organization_id}-${r.application_id}`);
    });
    return s;
  }, [rows]);
  const assignableApplicationsByOrg = useMemo(() => {
    const m = new Map();
    (context?.organizations || []).forEach((org) => {
      const orgId = Number(org.organization_id);
      const apps = clientOwnedApps.filter(
        (app) => !linkedRowSet.has(`${orgId}-${Number(app.application_id)}`)
      );
      if (apps.length) m.set(String(orgId), apps);
    });
    return m;
  }, [context, clientOwnedApps, linkedRowSet]);
  const assignableOrganizations = useMemo(
    () => (context?.organizations || []).filter((org) => assignableApplicationsByOrg.has(String(org.organization_id))),
    [context, assignableApplicationsByOrg]
  );
  const assignableApplications = useMemo(
    () => assignableApplicationsByOrg.get(String(organizationId)) || [],
    [assignableApplicationsByOrg, organizationId]
  );

  useEffect(() => {
    if (!organizationId) return;
    if (!assignableApplicationsByOrg.has(String(organizationId))) {
      setOrganizationId("");
      setApplicationId("");
      return;
    }
    if (
      applicationId &&
      !assignableApplications.some((app) => String(app.application_id) === String(applicationId))
    ) {
      setApplicationId("");
    }
  }, [organizationId, applicationId, assignableApplicationsByOrg, assignableApplications]);

  const handleAssign = async () => {
    if (!canAdd) return;
    if (!selectedClientId || !organizationId || !applicationId) {
      toast.error("Select organization and application.");
      return;
    }
    const alreadyLinked = linkedRowSet.has(`${organizationId}-${applicationId}`);
    if (alreadyLinked) {
      toast.error("This organization is already linked to this application.");
      return;
    }
    setAssigning(true);
    try {
      const res = await assignApplicationToOrganization(Number(selectedClientId), {
        organization_id: Number(organizationId),
        application_id: Number(applicationId),
      });
      toast.success(res.message || "Organization access assigned.");
      setApplicationId("");
      await loadContext(Number(selectedClientId));
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to assign organization access."
      );
    } finally {
      setAssigning(false);
    }
  };

  const toggleAccess = async (row) => {
    if (!canChangeStatus) return;
    const activeOrgId = Number(
      userData?.user_active_organization?.user_current_active_organization_id ??
        userData?.user_active_organization?.organization_id ??
        userData?.organizationuser?.organization_id ??
        userData?.organization?.organization_id ??
        0
    );
    const appKey = String(APPLICATION_KEY || "").toLowerCase();
    const appName = String(row.application_name || "").toLowerCase();
    const isCurrentAppRow =
      (appKey === "ems" && /education|ems/.test(appName)) ||
      (appKey === "pm" && /project|pm/.test(appName)) ||
      (appKey === "hr" && /human resources|human resource|hr/.test(appName));
    const isSelfOrgForCurrentApp =
      row.is_active &&
      row.organization_id === activeOrgId &&
      isCurrentAppRow;

    const key = `${row.organization_id}-${row.application_id}`;
    setUpdatingKey(key);
    try {
      const payload = {
        organization_id: row.organization_id,
        application_id: row.application_id,
      };
      const res = row.is_active
        ? await deactivateOrganizationApplication(Number(selectedClientId), payload)
        : row.link_exists
          ? await reactivateOrganizationApplication(Number(selectedClientId), payload)
          : await assignApplicationToOrganization(Number(selectedClientId), payload);
      toast.success(res.message || "Updated.");
      if (res.force_logout || isSelfOrgForCurrentApp) {
        logout();
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      await loadContext(Number(selectedClientId));
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to update organization application access."
      );
    } finally {
      setUpdatingKey("");
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Organization Application Access
      </Typography>
      {!canView && (
        <Alert severity="warning" sx={{ maxWidth: 1100, mx: "auto", mb: 2 }}>
          You do not have permission to view organization application access.
        </Alert>
      )}
      <Paper sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, px: 2, py: 1.25 }}>
          <Typography variant="caption" color="text.secondary">
            Client
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            {clientsLoading
              ? "Loading..."
              : clients.find((c) => String(c.client_id) === String(selectedClientId))
                  ? `${clients.find((c) => String(c.client_id) === String(selectedClientId))?.client_name || `Client #${selectedClientId}`} (Client ID: ${selectedClientId})`
                  : "No client available"}
          </Typography>
        </Box>

        {contextLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {contextError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {contextError}
          </Alert>
        )}

        {!canView ? null : !contextLoading && selectedClientId && !contextError && (
          <Box sx={{ mt: 2 }}>
            {canAdd && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Assign organization to application
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" }, gap: 1.5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="org-access-org-label">Organization</InputLabel>
                    <Select
                      labelId="org-access-org-label"
                      label="Organization"
                      value={organizationId}
                      onChange={(e) => setOrganizationId(String(e.target.value))}
                    >
                      <MenuItem value="">
                        <em>Select organization</em>
                      </MenuItem>
                      {assignableOrganizations.map((org) => (
                        <MenuItem key={org.organization_id} value={org.organization_id}>
                          {org.organization_name}
                          {org.organization_short_name ? ` (${org.organization_short_name})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel id="org-access-app-label">Application</InputLabel>
                    <Select
                      labelId="org-access-app-label"
                      label="Application"
                      value={applicationId}
                      onChange={(e) => setApplicationId(String(e.target.value))}
                    >
                      <MenuItem value="">
                        <em>Select application</em>
                      </MenuItem>
                      {assignableApplications.map((app) => (
                        <MenuItem key={app.application_id} value={app.application_id}>
                          {app.application_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={assigning || !organizationId || !applicationId}
                  >
                    {assigning ? "Assigning..." : "Assign Access"}
                  </Button>
                </Box>
              </Paper>
            )}

            {rows.length === 0 ? (
              <Alert severity="info">
                No organization applications found for this client. Use "Assign Application to Organization" first.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organization</TableCell>
                      <TableCell>Application</TableCell>
                      <TableCell>Status</TableCell>
                      {canChangeStatus && <TableCell align="center">Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const key = `${row.organization_id}-${row.application_id}`;
                      const busy = updatingKey === key;
                      return (
                        <TableRow key={key} hover>
                          <TableCell>
                            {row.organization_name}
                            {row.organization_short_name ? ` (${row.organization_short_name})` : ""}
                          </TableCell>
                          <TableCell>{row.application_name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.link_exists ? (row.is_active ? "Active" : "Inactive") : "Not linked"}
                              color={row.link_exists ? (row.is_active ? "success" : "default") : "warning"}
                              variant="outlined"
                            />
                          </TableCell>
                          {canChangeStatus && (
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  row.is_active
                                    ? "Toggle to inactive"
                                    : row.link_exists
                                      ? "Toggle to active"
                                      : "Assign and activate access"
                                }
                              >
                                <span>
                                  <Switch
                                    size="small"
                                    checked={row.is_active}
                                    onChange={() => toggleAccess(row)}
                                    disabled={busy}
                                    color={row.is_active ? "success" : "default"}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
